/**
 * AddGatewayDialog — QR scan or manual entry to link a gateway to a property.
 * Lazy-loads qr-scanner to avoid bloating main bundle.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Camera, Keyboard, CheckCircle2, AlertCircle } from 'lucide-react';
import { linkGatewayToProperty } from '@/services/gateway';
import { useQueryClient } from '@tanstack/react-query';

interface AddGatewayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  propertyName: string;
}

type Step = 'scan' | 'confirm' | 'linking' | 'success' | 'error';

function parseClaimCode(raw: string): string | null {
  // QR format: wt://claim/{claim_code}
  const qrMatch = raw.match(/^wt:\/\/claim\/(.+)$/);
  if (qrMatch) return qrMatch[1];
  // Manual: starts with WT-
  const trimmed = raw.trim();
  if (/^WT-[A-Za-z0-9]+$/.test(trimmed)) return trimmed;
  return null;
}

export function AddGatewayDialog({ open, onOpenChange, propertyId, propertyName }: AddGatewayDialogProps) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('scan');
  const [claimCode, setClaimCode] = useState('');
  const [manualInput, setManualInput] = useState('');
  const [useManual, setUseManual] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [cameraError, setCameraError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const scannerRef = useRef<unknown>(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep('scan');
      setClaimCode('');
      setManualInput('');
      setUseManual(false);
      setErrorMsg('');
      setCameraError(false);
    }
  }, [open]);

  // Initialize QR scanner when in scan mode
  useEffect(() => {
    if (!open || step !== 'scan' || useManual || cameraError) return;

    let scanner: { stop: () => void; destroy: () => void } | null = null;
    let cancelled = false;

    async function initScanner() {
      try {
        const QrScanner = (await import('qr-scanner')).default;
        if (cancelled || !videoRef.current) return;

        scanner = new QrScanner(
          videoRef.current,
          (result: { data: string }) => {
            const code = parseClaimCode(result.data);
            if (code) {
              setClaimCode(code);
              setStep('confirm');
              scanner?.stop();
            }
          },
          {
            preferredCamera: 'environment',
            highlightScanRegion: true,
            highlightCodeOutline: true,
          }
        );
        scannerRef.current = scanner;
        await scanner.start();
      } catch {
        if (!cancelled) setCameraError(true);
      }
    }

    initScanner();
    return () => {
      cancelled = true;
      scanner?.stop();
      scanner?.destroy();
      scannerRef.current = null;
    };
  }, [open, step, useManual, cameraError]);

  const handleManualSubmit = useCallback(() => {
    const code = parseClaimCode(manualInput);
    if (code) {
      setClaimCode(code);
      setStep('confirm');
    } else {
      setErrorMsg('Invalid claim code. Expected format: WT-XXXXXX');
    }
  }, [manualInput]);

  const handleLink = useCallback(async () => {
    setStep('linking');
    setErrorMsg('');
    try {
      await linkGatewayToProperty(claimCode, propertyId);
      queryClient.invalidateQueries({ queryKey: ['gateway-device', propertyId] });
      setStep('success');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to link gateway');
      setStep('error');
    }
  }, [claimCode, propertyId, queryClient]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-wt-bg-secondary border-border">
        <DialogHeader>
          <DialogTitle className="text-wt-text-primary">
            {step === 'success' ? 'Gateway Linked' : 'Add Gateway'}
          </DialogTitle>
        </DialogHeader>

        {/* Step 1: Scan or Manual Entry */}
        {step === 'scan' && (
          <div className="space-y-4">
            {!useManual && !cameraError ? (
              <div className="space-y-3">
                <div className="relative rounded-lg overflow-hidden bg-black aspect-square max-h-64 mx-auto">
                  <video ref={videoRef} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 border-2 border-dashed border-wt-green-primary/30 m-8 rounded-lg" />
                </div>
                <p className="text-xs text-wt-text-secondary text-center">
                  Point camera at the QR code on the gateway
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setUseManual(true)}
                  className="w-full"
                >
                  <Keyboard className="w-3.5 h-3.5 mr-1.5" />
                  Enter Code Manually
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {cameraError && (
                  <p className="text-xs text-wt-text-secondary text-center">
                    Camera not available. Enter the claim code manually.
                  </p>
                )}
                <Input
                  value={manualInput}
                  onChange={(e) => {
                    setManualInput(e.target.value);
                    setErrorMsg('');
                  }}
                  placeholder="WT-XXXXXX"
                  className="font-mono-numeric"
                  onKeyDown={(e) => e.key === 'Enter' && handleManualSubmit()}
                />
                {errorMsg && (
                  <p className="text-xs text-wt-red-primary">{errorMsg}</p>
                )}
                <div className="flex gap-2">
                  {!cameraError && (
                    <Button variant="outline" size="sm" onClick={() => setUseManual(false)} className="flex-1">
                      <Camera className="w-3.5 h-3.5 mr-1.5" />
                      Use Camera
                    </Button>
                  )}
                  <Button size="sm" onClick={handleManualSubmit} className="flex-1">
                    Continue
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Confirm */}
        {step === 'confirm' && (
          <div className="space-y-4">
            <div className="rounded-lg bg-wt-bg-tertiary p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-wt-text-secondary">Property</span>
                <span className="text-wt-text-primary font-medium">{propertyName}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-wt-text-secondary">Claim Code</span>
                <span className="text-wt-text-primary font-mono-numeric">{claimCode}</span>
              </div>
            </div>
            <p className="text-sm text-wt-text-secondary text-center">
              Link this gateway to {propertyName}?
            </p>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => { setStep('scan'); setClaimCode(''); }}>
                Back
              </Button>
              <Button onClick={handleLink}>Link Gateway</Button>
            </DialogFooter>
          </div>
        )}

        {/* Step 3: Linking */}
        {step === 'linking' && (
          <div className="py-8 flex flex-col items-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            <p className="text-sm text-wt-text-secondary">Linking gateway...</p>
          </div>
        )}

        {/* Step 4: Success */}
        {step === 'success' && (
          <div className="py-4 space-y-4">
            <div className="flex flex-col items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-wt-green-primary" />
              <p className="text-sm text-wt-text-primary text-center">
                Gateway linked. Power on the device to begin registration.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </DialogFooter>
          </div>
        )}

        {/* Step 5: Error */}
        {step === 'error' && (
          <div className="py-4 space-y-4">
            <div className="flex flex-col items-center gap-3">
              <AlertCircle className="w-8 h-8 text-wt-red-primary" />
              <p className="text-sm text-wt-red-primary text-center">{errorMsg}</p>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setStep('scan')}>
                Try Again
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
